import React from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import { TILE_HEIGHT } from './tileMetrics';

/**
 * The dashed "Parents recorded" chip that takes the place of a tile once the
 * relatives exist. Static text, never focusable — a disabled button that can
 * never become enabled is a dead end for a keyboard user, and the reason is part
 * of the text rather than a tooltip nobody can reach.
 *
 * Exactly TILE_HEIGHT, so a row does not change height when a tile becomes a chip
 * — and so "Grandparents recorded", which wraps to two lines in the narrow
 * sidebar, cannot make its half of the row taller than the other.
 *
 * IT MUST KEEP `data-va-anchor` AND `aria-disabled="true"`. The
 * `pedigree-build-basic` Copilot walkthrough completes its Parents and
 * Grandparents steps on `done: { type: 'anchor-disabled' }`, and the overlay
 * computes `disabled = !!el && (… || el.getAttribute('aria-disabled') ===
 * 'true')`. An ABSENT anchor is never "disabled", so a chip without these two
 * attributes stalls that tour forever. `walkthroughAnchorsDom.test.jsx` also
 * asserts all seven `pedigree.add-relative.*` anchors render in the sidebar.
 */
const RecordedChip = ({ label, vaAnchor }) => (
  <Box
    data-va-anchor={vaAnchor}
    aria-disabled="true"
    sx={{
      height: '100%',
      minHeight: TILE_HEIGHT,
      // A div is content-box by default, so the dashed border would otherwise add
      // 2px and leave the chip taller than the tile it replaces.
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.75,
      px: 1,
      borderRadius: 1.5,
      border: '1px dashed',
      borderColor: 'divider',
      bgcolor: (theme) => alpha(theme.palette.success.main, 0.06),
      color: 'text.secondary'
    }}
  >
    <CheckIcon
      aria-hidden="true"
      sx={{
        fontSize: 20,
        color: 'success.main',
        // MUI icons are filled paths, so there is no font-weight to set — stroking
        // the same path in its own colour is what actually thickens the tick.
        stroke: 'currentColor',
        strokeWidth: 1.6,
        flexShrink: 0
      }}
    />
    <Typography
      component="span"
      sx={{ fontSize: 12.5, fontWeight: 500, lineHeight: 1.25 }}
    >
      {label}
    </Typography>
  </Box>
);

export default RecordedChip;
